import { z } from 'zod'

/** Someone who voted for an option; name and photo are null once the account is deleted. */
export const pollVoterSchema = z.object({
  id: z.string().uuid(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})

export type PollVoter = z.infer<typeof pollVoterSchema>

export const pollOptionDtoSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  voteCount: z.number(),
  hasVoted: z.boolean(),
  editedAt: z.string().nullable(),
  voters: z.array(pollVoterSchema),
})

export type PollOptionDto = z.infer<typeof pollOptionDtoSchema>

export const pollDtoSchema = z.object({
  id: z.string().uuid(),
  planId: z.string().uuid(),
  question: z.string(),
  pollType: z.enum(['text', 'date', 'location']),
  allowMultiple: z.boolean(),
  isClosed: z.boolean(),
  closesAt: z.string().nullable(),
  createdById: z.string().uuid().nullable(),
  createdByName: z.string().nullable(),
  options: z.array(pollOptionDtoSchema),
  /** People who voted at all — the base for percentages on multiple-choice polls. */
  voterCount: z.number().int(),
  createdAt: z.string(),
  editedAt: z.string().nullable(),
})

export type PollDto = z.infer<typeof pollDtoSchema>

/** How many of the people who voted picked this option, 0–1; per person, so multiple-choice shares can add up past 1. */
export function pollOptionShare(
  option: Pick<PollOptionDto, 'voteCount'>,
  poll: Pick<PollDto, 'voterCount'>,
): number {
  return poll.voterCount > 0 ? Math.min(1, option.voteCount / poll.voterCount) : 0
}

export const createPollInputSchema = z.object({
  question: z.string().min(1).max(200),
  pollType: z.enum(['text', 'date', 'location']),
  allowMultiple: z.boolean(),
  closesAt: z.string().datetime().optional(),
  options: z.array(z.string().min(1).max(100)).min(2).max(20),
})

export type CreatePollInput = z.infer<typeof createPollInputSchema>

export const pollListResponseSchema = z.object({
  polls: z.array(pollDtoSchema),
})

export const createPollResponseSchema = z.object({
  poll: pollDtoSchema,
})

export const pollVoteRequestSchema = z.object({
  optionId: z.string().uuid(),
})

export const updatePollInputSchema = z.object({
  question: z.string().min(1).max(200),
})

export type UpdatePollInput = z.infer<typeof updatePollInputSchema>

export const updatePollOptionInputSchema = z.object({
  text: z.string().min(1).max(100),
})

export type UpdatePollOptionInput = z.infer<typeof updatePollOptionInputSchema>

export type PollListResponse = z.infer<typeof pollListResponseSchema>
export type CreatePollResponse = z.infer<typeof createPollResponseSchema>
export type PollVoteRequest = z.infer<typeof pollVoteRequestSchema>
